import React, { useState, useEffect } from "react";
import { Alert, Button, Checkbox, InputNumber, Select, Spin, Switch } from "antd";
import { FloppyDiskIcon, PlusCircleIcon, MinusCircleIcon } from "@phosphor-icons/react";
import completedIcon from "../../../../assets/img/icons/completed-icon.png";
import errorIcon from "../../../../assets/img/icons/error-icon.png";
import { useViewerStore } from "../../../../store/store";
import { isMobile } from "../../../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common";
import { AlertType, CalculationType, EquipmentType } from "../../../../@types/enums";
import { EquipmentOptionData, EquipmentItem, EquipmentOptions, InverterCalculatorData, RackingQuantitiesResponse } from "../../../../@types/types";
import { useCurrentUser } from "../../../../contexts/UserContext";
import packingSlipService from "../../../../services/packingSlipService";
import { emptyPackingSlipRows } from "../../../../Constants";
import { cloneDeep } from "lodash";
import { LoadingContext } from "../../../../store/loadingMessageStore";
import { useCalculationStates } from "../../../../hooks/calculationStateHook";
import { ACCESS_MULTIPLIERS, FACE_IDEAL_API, MODULE_GRAPH, SHADE_CALCULATIONS } from "../../../../services/siteCalculationState";

const { Option } = Select;

const PackingSlip: React.FC = () => {
    const { hasCapability } = useCurrentUser();
    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const activeModule = useViewerStore((state) => state.activeModule);
    const moduleCount = useViewerStore((state) => state.modeCount);
    const selectedDesignIndex = useViewerStore((state) => state.selectedDesignIndex);
    const roofInfoArr = useViewerStore(state => state.roofInfoArr);
    const refreshingShade = useViewerStore(state => state.refreshingShade);
    const financePartnerDetails = useViewerStore(state => state.financePartnerDetails);
    const designList = useViewerStore((state) => state.designList);
    const activeDesign = designList && designList[selectedDesignIndex];
    const isRoofLabelOverrideByUser = useViewerStore(state => state.isRoofLabelOverrideByUser);

    const [isLoadingData, setIsLoading] = useState(false);
    const [isSavingData, setIsSavingData] = useState(false);
    const [equipmentOptions, setEquipmentOptions] = useState<EquipmentOptions>({});
    const [equipmentList, setEquipmentList] = useState<EquipmentItem[]>(emptyPackingSlipRows);
    const [arrayOptions, setArrayOptions] = useState<number[]>([]);
    const [boxURL, setBoxURL] = useState<string | null>(null);
    const [isMaxInverter, setIsMaxInverter] = useState(false);

    // Domestic content states
    const [domesticContentPercent, setDomesticContentPercent] = useState(0);
    const [isDomesticContent, setIsDomesticContent] = useState(false);
    const [isDomesticContentoading, setIsDomesticContentLoading] = useState(false);
    const [isAlreadySaved, setIsAlreadySaved] = useState(true);
    const [overrideEnabled, setOverrideEnabled] = useState(false);
    const isNonIGSOpportunity = () => !financePartnerDetails?.name?.toLowerCase().includes('igs');
    const financePartnerContractId = String(financePartnerDetails?.contractId ?? '').trim();
    const [isLoadingQty, setIsLoadingQty] = useState(false);
    const isReadOnly = ()=> isAlreadySaved && !hasCapability("Re_Submit_PackingSlip");
    const { needsUpdate } = useCalculationStates(
        [FACE_IDEAL_API, MODULE_GRAPH, SHADE_CALCULATIONS, ACCESS_MULTIPLIERS] as CalculationType[], { useCalculation: true }); // { useCalculation: true }
    const shadeNotReady: boolean = Object.values(needsUpdate).includes(true) || refreshingShade;

    const getDesigLockStatus = (): boolean => {
        const isUserAllowed = (hasCapability('Finalize') && hasCapability('Stage_4_Post_Contract_Finalize'));
        return selectedDesignIndex === -1 || !isUserAllowed;
    }

    const isRackingPart = (type: EquipmentType) => type === EquipmentType.Rail || type === EquipmentType.Splice || type === EquipmentType.MidClamp || type === EquipmentType.EndClamp || type === EquipmentType.MlpeBracket;

    // Table headers
    const thArr = [
        { key: 'type', long: 'Equipment Type', short: 'Type' },
        { key: 'model', long: 'Equipment Model', short: 'Model' },
        { key: 'manufacturer', long: 'Manufacturer', short: 'Mfg' },
        { key: 'quantity', long: 'Quantity', short: 'Qty' },
        { key: 'array', long: 'Arrays', short: 'Arrays' },
    ];

    // Get equipment options for dropdown, filter by racking model if racking part
    const getOptions = (type: EquipmentType, rackingModel?: string, options?: any): EquipmentOptionData[] => {
        const rackingModelValue = rackingModel ?? equipmentList.find(eq => eq.category === EquipmentType.RackingSystem)?.model;
        return isRackingPart(type) ? (options ?? equipmentOptions).rackingSystems?.find((e: EquipmentOptionData) => e.model === rackingModelValue)?.parts[`${type}s` as keyof EquipmentOptions] ?? [] : (options ?? equipmentOptions)[`${type}s` as keyof EquipmentOptions] ?? [];
    };

    const getInverterCount = (): number =>
        equipmentList?.filter(item => item.category === EquipmentType.Inverter).length ?? 0;


    //=================== CHANGE HANDLERS ==================================

    function handleChangeModel(name: string, category: EquipmentType, value: string) {

        const getMasterData = (partType?: EquipmentType): EquipmentOptionData | undefined => 
            partType && value !== '' ?  getOptions(partType, value).find(e => e.default) : value !== '' ? getOptions(category).find(item => item.model === value) : undefined;

        const newPackingSlipData = equipmentList.map((eq) =>
            eq.name === name ? {
                ...eq,
                model: value,
                manufacturer: getMasterData()?.manufacturer,
                financePartnerEquipmentId: getMasterData()?.financePartnerModelId,
                quantity: value === '' ? undefined : eq.category === EquipmentType.Optimizer ? moduleCount : eq.quantity ?? 1
            } : category === EquipmentType.RackingSystem && isRackingPart(eq.category) 
                ? {
                    ...eq,
                    model: getMasterData(eq.category)?.model,
                    manufacturer: getMasterData(eq.category)?.manufacturer,
                    quantity: value === '' ? undefined : eq.quantity ?? 1,
                    financePartnerEquipmentId: getMasterData(eq.category)?.financePartnerModelId ?? '',
                } : eq
        )
        setEquipmentList(newPackingSlipData);
        loadDomesticContent(newPackingSlipData, financePartnerDetails?.id);
    }

    function handleChangeQuantity(name: string, value: number) {
        setEquipmentList((prev) =>
            prev.map((eq) =>
                eq.name === name ? { ...eq, quantity: value } : eq
            )
        )
    }

    // Use available model/array dropdown values when adding
    function handleAddRow() {
        // Return if maximum inverter rows reached
        const count = getInverterCount();
        if (count >= 4) return;

        // Adds new inverter after the last inverter
        const lastIndex = equipmentList.findIndex(item => item.name === (count === 1 ? 'Inverter' : `Inverter${count}`));
        const newItem: EquipmentItem = {
            category: EquipmentType.Inverter,
            name: `Inverter${count + 1}`,
            overrideEnabled: false,
        };
        const newList = [...equipmentList];
        newList.splice(lastIndex + 1, 0, newItem);
        setEquipmentList(newList);
    };

    const handleRemoveRow = (name: string) => {
        const newEqList: EquipmentItem[] = (() => {
            let inverterIndex = 0;
            return equipmentList
                .filter(eq => eq.name !== name) // Filter out removed inverter
                .map(eq => {
                    if (eq.category !== EquipmentType.Inverter) return eq;
                    inverterIndex++;
                    return { ...eq, name: inverterIndex === 1 ? 'Inverter' : `Inverter${inverterIndex}` };
                });
        })();

        setEquipmentList(newEqList);
        loadDomesticContent(newEqList, financePartnerDetails?.id);
    };

    //  To chunk array options by 6 for row layout:
    const getArrayByRow = (size: number = 6): number[][] =>
        Array.from({ length: Math.ceil(arrayOptions.length / size) }, (_, i) =>
            arrayOptions.slice(i * size, i * size + size)
        );


    // Multi-select change handler
    const handleArrayChange = (inverterName: string, roofNumbers: number[]) =>
        setEquipmentList(prev =>
            prev.map(eq => eq.name === inverterName ? { ...eq, roofNumbers } : eq)
        );

    //=================== SERVICE CALL FUNCTIONS ==================================


    async function loadInitialData() {
        setIsLoading(true);

        // To load master data for dropdowns
        const eqOptions = await packingSlipService.fetchEqipmentOptions(financePartnerDetails?.id || 22);
        setEquipmentOptions(eqOptions ?? {});

        const moduleMaster = getOptions(EquipmentType.Module, undefined, eqOptions).find(m => m.salesforceOpportunityListing === activeModule.text);
        
        const getSavedOrDefaultValues = async () => {

            // To fetch Saved equipments list
            const data = await packingSlipService.fetchPackingSlipData(activeDesign?.DesignID);
            setIsAlreadySaved(!!data);
            setBoxURL(null);

            // If saved equipments data available
            if (data) {
                const savedEqList = data.equipments;
                setBoxURL(data.boxURL ?? null);

                return cloneDeep(emptyPackingSlipRows).flatMap((eq: EquipmentItem) => {
                    if (eq.category === EquipmentType.Module) {
                        // Adds active module & module count from design to the equipments list
                        return {
                            ...eq,
                            model: activeModule.text,
                            manufacturer: activeModule.manufacturer,
                            financePartnerEquipmentId: moduleMaster?.financePartnerModelId ?? '',
                            quantity: moduleCount
                        };
                    } else if (eq.category === EquipmentType.Inverter) {
                        // Adds all inverters from the saved data
                        const savedInverters = savedEqList?.filter(i => i.category === eq.category);
                        if (!savedInverters?.length) return eq;
                        return savedInverters.map((inv, idx) => ({
                            ...inv,
                            name: `Inverter${idx === 0 ? '' : idx + 1}`,
                            roofNumbers: inv.roofNumbers ?? [],
                        }));

                    } else {
                        let savedData = savedEqList?.find(i => i.category === eq.category);
                        if (!savedData) return eq;
                        const quantity = eq.category === EquipmentType.Optimizer
                            ? savedData?.model ? moduleCount : undefined
                            : savedData?.quantity ?? (savedData?.model ? 1 : undefined);
                        return { ...savedData, quantity };
                    }
                });

            } else { // To load default values from Excel

                // Load default values
                const data: InverterCalculatorData = {
                    activeModule,
                    moduleCount,
                    arrayCount: roofInfoArr.filter(r => r.modules > 0).length,
                    financePartnerDetails: financePartnerDetails!,
                    roofInfoArr,
                    eqOptions,
                }

                const defaultValues = await packingSlipService.getDefaultValues(data);
                if (defaultValues.error && defaultValues.error !== '') console.log('Excel Error:', defaultValues.error);
                console.log('defaultValues:', defaultValues);

                return cloneDeep(emptyPackingSlipRows).flatMap(eq => {
                    if (eq.category === EquipmentType.Module) {
                        // Adds active module & module count from design to the equipments list
                        return { ...eq,
                            model: activeModule.text,
                            manufacturer: activeModule.manufacturer,
                            financePartnerEquipmentId: moduleMaster?.financePartnerModelId ?? '',
                            quantity: moduleCount
                        };
                    } else if (eq.category === EquipmentType.Inverter) {
                        const defultInverters = defaultValues[EquipmentType.Inverter] ?? [];
                        return !defultInverters.length ? eq : defultInverters;
                    } else if (eq.category === EquipmentType.Optimizer || eq.category === EquipmentType.RackingSystem) {
                        const defaultModel = defaultValues[eq.category];
                        if (!defaultModel) return eq;
                        // Find equipment details from master data
                        const masterData = eq.category === EquipmentType.RackingSystem ?
                            eqOptions?.rackingSystems?.find(e => e.salesforceOpportunityListing === defaultModel)
                            : getOptions(eq.category, '', eqOptions).find(e => e.model === defaultModel);
                        if (!masterData) return eq;
                        return {
                            ...eq,
                            model: masterData.model,
                            manufacturer: masterData.manufacturer,
                            financePartnerEquipmentId: masterData?.financePartnerModelId ?? '',
                            quantity: eq.category === EquipmentType.Optimizer ? moduleCount : 1,
                        };
                    } else if (isRackingPart(eq.category)) {
                        const defaultRacking = defaultValues[EquipmentType.RackingSystem];
                        const defaultRackingModel = eqOptions?.rackingSystems?.find(e => e.salesforceOpportunityListing === defaultRacking)?.model;
                        
                        const masterData = defaultRackingModel ? getOptions(eq.category, defaultRackingModel, eqOptions).find(e => e.default) : undefined;
                        if (!masterData) return eq;
                        return {
                            ...eq,
                            model: masterData.model,
                            manufacturer: masterData.manufacturer,
                            financePartnerEquipmentId: masterData?.financePartnerModelId ?? '',
                            quantity: 1,
                        };
                    }
                    return eq; // Adds blank row if no default data
                });

            }
        }


        let newEqList: EquipmentItem[] = await getSavedOrDefaultValues();
        (hasDuplicateArrays(newEqList)) && setOverrideEnabled(true);
        setEquipmentList(newEqList);

        // Recalculate Domestic Content
        await loadDomesticContent(newEqList, financePartnerDetails?.id);
        setIsLoading(false);
    }

    async function loadDomesticContent(items: EquipmentItem[], finPartnerId?: number | null) {
        // Checks if minimum required equipments are selected
        const requiredTypes = [EquipmentType.Module, EquipmentType.Inverter, EquipmentType.Optimizer, EquipmentType.RackingSystem];
        const isValid = requiredTypes.every(type => items?.some(item => item.category === type && !!item.model));
        if (!isValid) {
            setIsDomesticContent(false);
            setDomesticContentPercent(0);
            return;
        }

        setIsDomesticContentLoading(true);
        const partCount = items.filter((item) => !!item.model).length;
        const partsMap = items.reduce((acc, item) => {
            if (!!item.model && item.category !== EquipmentType.Inverter) {
                acc[item.name] =  item.model;
            }
            return acc;
        }, {} as any);

        const inverters = items.filter(item => item.category === EquipmentType.Inverter);
        
        for (let i = 0; i < 4; i++) {
            partsMap[`Inverter${i === 0 ? '' : i + 1}`] = inverters.find(item => item.name === `Inverter${i === 0 ? '' : i + 1}`)?.model ?? '';
        }

        const data = {
            "financePartnerId": finPartnerId ? finPartnerId.toString() : '22',
            "environment": import.meta.env.VITE_EXCEL_ENV_NAME,
            "partCount": partCount.toString(),
            "parts": partsMap,
        };

        const result = await packingSlipService.fetchDomesticContent(data);
        setIsDomesticContent(result?.systemIsDomestic ?? false);
        setDomesticContentPercent(result?.systemDCPercentage ?? 0);
        setIsDomesticContentLoading(false);
    }

    const handleCalculateRackingQuantities = async () => {
        if (!activeDesign?.DesignID) return;
        try {
            setIsLoadingQty(true);
            const rackResponse: RackingQuantitiesResponse =
                await packingSlipService.calculateRackingQuantities(activeDesign.DesignID);
 
            const rackingMap: Record<string, number | undefined> = {
                "Rail": rackResponse.rail,
                "Splice": rackResponse.splice,
                "Mid-Clamp": rackResponse.midClamp,
                "End-Clamp": rackResponse.endClamp,
                "MLPE Bracket": rackResponse.mlpeBracket,
            };
 
            setEquipmentList(prev =>
                prev.map(eq => ({ ...eq, quantity: rackingMap[eq.name] ?? eq.quantity })));
        } catch (e: any) {
            sceneViewer.viewerAlert({
                show: true,
                title: "No Racking Quantities",
                message: e.message || "Failed to calculate racking quantities. No quantities available in the database for this design.",
                messageType: AlertType.warning,
                isModal: false,
            });
        } finally {
            setIsLoadingQty(false);
        }
    };

    //=================== ALERT HANDLERS ==================================
    const openSaveAlert = () => {
        const assignedArrays = new Set<number>();

        equipmentList.forEach(eq => {
            if (eq.category === EquipmentType.Inverter && Array.isArray(eq.roofNumbers)) {
                eq.roofNumbers.forEach(n => assignedArrays.add(n));
            }
        });

        const unassignedArrays = arrayOptions.filter(a => !assignedArrays.has(a));
        if (unassignedArrays.length > 0) {
            const unassignedString = unassignedArrays.length === 1
                ? `Array ${unassignedArrays[0]} is`
                : `Arrays ${unassignedArrays.slice(0, -1).join(", ")} & ${unassignedArrays.slice(-1)} are`;
            return sceneViewer.viewerAlert({
                show: true,
                title: "Warning",
                message: `${unassignedString} currently unassigned.\n Please ensure each array is linked to an inverter.`,
                messageType: AlertType.warning,
                isModal: false,
            });
        }

        const inverterWithoutModel = equipmentList.filter(eq => eq.category === EquipmentType.Inverter && !eq.model);
        if (inverterWithoutModel.length > 0) {
            const inverterNames = inverterWithoutModel.map(inv => inv.name);
            const unassignedString = inverterWithoutModel.length === 1
                ? `${inverterNames[0]}` : `${inverterNames.slice(0, -1).join(", ")} & ${inverterNames.slice(-1)}`;
            return sceneViewer.viewerAlert({
                show: true,
                title: "Warning",
                message: `Inverter model has not been selected for ${unassignedString}.`,
                messageType: AlertType.warning,
                isModal: false,
            });
        }

        const inverterWithoutArray = equipmentList.filter(eq => eq.category === EquipmentType.Inverter && eq.roofNumbers?.length === 0);
        if (inverterWithoutArray.length > 0) {
            const inverterNames = inverterWithoutArray.map(inv => inv.name);
            const unassignedString = inverterWithoutArray.length === 1
                ? `${inverterNames[0]} is` : `${inverterNames.slice(0, -1).join(", ")} & ${inverterNames.slice(-1)} are`;
            return sceneViewer.viewerAlert({
                show: true,
                title: "Warning",
                message: `${unassignedString} not attached to any arrays.\n Please select an array, or remove this inverter from the list.`,
                messageType: AlertType.warning,
                isModal: false,
            });
        }

        sceneViewer.viewerAlert({
            show: true,
            title: "Save & Generate Packing Slip",
            message: <>
                <p className="report-text">Click Confirm to Save and Generate Packing Slip PDF. </p>
                {!hasCapability("Re_Submit_PackingSlip") && <p className="report-text">Please note that you will not have permission to make edits after submission.</p>}
                <div className={"modal-bottom-action-button-container"}>
                    <Button
                        id={"confirmSiteReportBtn"}
                        type="primary"
                        size="large"
                        className="editor__control--finalize modal-confirm-button"
                        onClick={()=> {
                            sceneViewer.viewerAlert({ show: false });
                            savePackingSlip();
                        }}
                    >
                        Confirm
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        className="editor__control--finalize modal-cancel-button"
                        onClick={()=> sceneViewer.viewerAlert({ show: false })}
                    >
                        Cancel
                    </Button>
                </div>
            </>,
            messageType: AlertType.info,
            isModal: true
        })
    }

    // Open override alert
    const openOverrideAlert = () => {
        sceneViewer.viewerAlert({
            show: true,
            title: "Confirm Override",
            message: <>
                <p className="report-text">
                    You have chosen to permit multiple inverters on a single array. Would you like to continue?
                </p>
                <div className="modal-bottom-action-button-container">
                    <Button
                        id="confirmOverrideBtn"
                        type="primary"
                        size="large"
                        className="editor__control--finalize modal-confirm-button"
                        onClick={() => {
                            sceneViewer.viewerAlert({ show: false });
                            setOverrideEnabled(true);
                        }}
                    >
                        Yes
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        className="editor__control--finalize modal-cancel-button"
                        onClick={() => {
                            sceneViewer.viewerAlert({ show: false });
                            setOverrideEnabled(false);
                        }}
                    >
                        No
                    </Button>
                </div>
            </>,
            messageType: AlertType.info,
            isModal: true
        });
    };

    const savePackingSlip = async () => {
        setIsSavingData(true);
        const loadingContext = new LoadingContext();
        loadingContext.setDefaultTitle('Saving Packing Slip...');
        loadingContext.setMessage({ message: 'Saving Packing Slip data and generating Packing Slip PDF' });
        const opId = sessionStorage.getItem('SalesForceId');
        const data = {
            sF_OpportunityId: opId,
            design_Id: activeDesign.DesignID,
            finance_Partner: financePartnerDetails?.name?.trim() || 'Good Leap',
            domestic_Content_Perc: domesticContentPercent ?? 0,
            equipments: equipmentList.filter(eq => !!eq.model)
                .map((eq, idx) => ({
                    ...eq,
                    id: idx,
                    ...(eq.roofNumbers?.length && { array_Number: eq.roofNumbers.filter(r => arrayOptions.some(a => a === r)) }),
                }))
        };
        const { success, FileName, URL } = await packingSlipService.savePackingSlipData(data);
        if (success) {
            setIsAlreadySaved(true);
            setBoxURL(URL);
            // Reset qty if equipment models are not selected
            setEquipmentList(prev => prev.map(eq => eq.model ? eq : { ...eq, quantity: undefined }));
        }
        setIsSavingData(false);
        loadingContext.dispose();
        sceneViewer.viewerAlert({
            show: true,
            title: success ? 'Saved' : 'Failed',
            message: success ? (URL === '' ?
                <>
                    <p className="report-text">Packing Slip saved but failed to generate/ upload the pdf</p>

                    <img
                        src={errorIcon}
                        alt={"Error Icon"}
                        width={50}
                        height={50}
                    />
                </>
                : <>
                    <p className="report-text">Packing Slip successfully generated and uploaded to the Box folder</p>
                    <p
                        className="report-text"
                        style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                    >
                        <img
                            src={completedIcon}
                            alt={"Completed Icon"}
                            width={16}
                            height={16}
                            style={{ width: 16, height: 16, objectFit: "contain", verticalAlign: "middle" }}
                        />
                        <a href={URL} target="_blank" rel="noopener noreferrer">{FileName}</a>
                    </p>
                </>) : (<>
                    <p className="report-text">Failed to save the Packing Slip</p>

                    <img
                        src={errorIcon}
                        alt={"Error Icon"}
                        width={50}
                        height={50}
                    />
                </>),
            messageType: success ? (URL === '' ? AlertType.warning : AlertType.success) : AlertType.error,
            isModal: false,
        });
    };

    // Get all inverter indices
    const getInverterIndices = () => {
        return equipmentList
            .map((item, index) => (item.category === EquipmentType.Inverter ? index : null))
            .filter((index) => index !== null);
    }

    const hasDuplicateArrays = (eqList: EquipmentItem[]): boolean => {
        const counts = new Map<number, number>();
        for (const eq of eqList) {
            if (eq.category !== EquipmentType.Inverter || !Array.isArray(eq.roofNumbers)) continue;
            // Use a Set to avoid double-counting the same array on the same inverter
            for (const num of new Set(eq.roofNumbers)) {
                const count = (counts.get(num) ?? 0) + 1;
                if (count > 1) return true;
                counts.set(num, count);
            }
        }
        return false;
    };


    const isArrayTaken = (inverterName: string, arrayNum: number) => {
        if (overrideEnabled) {
            return false; // Allow multiple selection when override ON
        }

        const currentInverter = equipmentList.find(eq => eq.name === inverterName);
        const currentRoofNumbers = Array.isArray(currentInverter?.roofNumbers) ? currentInverter.roofNumbers : [];

        return equipmentList.some(
            eq =>
                eq.category === EquipmentType.Inverter &&
                eq.name !== inverterName &&
                Array.isArray(eq.roofNumbers) &&
                eq.roofNumbers.includes(arrayNum) &&
                !currentRoofNumbers.includes(arrayNum)
        );
    }

    //=================== REACT USE EFFECTS ==================================

    useEffect(() => {
        loadInitialData();
        const solarArrays = roofInfoArr
            .filter(r => r.modules > 0)
            .map(r => Number(r.nameOverride ?? r.roofNum))
            .sort((a, b) => a - b);
        setArrayOptions(solarArrays);
    }, [roofInfoArr]);

    useEffect(() => {
        setIsMaxInverter(getInverterCount() >= 4);
    }, [equipmentList]);

    // Update Module & Optimizer quantity on design updated
    useEffect(() => {
        setEquipmentList((prev) => prev.map(eq =>
            !!eq.model && (eq.category === EquipmentType.Module || eq.category === EquipmentType.Optimizer)
                ? { ...eq, quantity: moduleCount } : eq));
    }, [moduleCount]);

    //===================  REACT COMPONENT ==================================

    return (
        shadeNotReady||isLoadingData ? <div className="packing-slip-loader"><Spin size="large" /><span className="theme-based-text-color">Loading Packing Slip Data...</span></div>
        : <div className="tab-content-container">
            <div className="packing-slip-header">
                <div className="info-group">
                    {selectedDesignIndex !== -1 && !isRoofLabelOverrideByUser ? <div className="design-identifiers">
                        <div><label>Design No: </label>  <span>{activeDesign?.Design_No ?? ""} </span></div>
                        <div><label>Version No: </label> <span>{activeDesign?.Version_No ?? ""}</span></div>
                    </div> :
                        <Alert
                            style={{ color: "indianred" }}
                            message={isRoofLabelOverrideByUser ? 'The roof labels adjusted, please finalize the design to save & generate Packing Slip' : 'The design should be finalized to save & generate Packing Slip'}
                            type={'warning'}
                            showIcon
                        />
                    }
                    <div className="finance-partner">
                        <label>Finance Partner: </label>
                        <span>{financePartnerDetails?.name?.trim() || 'Not selected'}</span>
                        <span className="finance-partner__contract-id">
                            <label>Contract ID: </label>
                            <span>{financePartnerContractId ?? ''}</span>
                        </span>
                    </div>
                </div>

                {boxURL && (<div>View in Box: <a href={boxURL} target="_blank" rel="noopener noreferrer"> PackingSlip.pdf </a></div>)}

                <div className="domestic-content-bar">
                    <div className="domestic-label">Domestic Content:</div>
                    <div className={`domestic-value`} style={{ background: isDomesticContent ? 'seagreen' : 'darkred' }} >
                        {isDomesticContentoading ? <Spin /> :
                            <span>
                                {domesticContentPercent ?? 0} %
                            </span>}
                    </div>

                    {!isReadOnly() && <Button
                        // size={"small"}
                        loading={isSavingData}
                        title={"Save & Generate Packing Slip"}
                        icon={<FloppyDiskIcon size={15} />}
                        className={"packing-slip-table-save-button"}
                        type="primary"
                        onClick={openSaveAlert}
                        disabled={getDesigLockStatus() || isRoofLabelOverrideByUser}
                        style={{
                            marginLeft: 10,
                            alignItems: "center",
                        }}
                    >
                        Save & Generate PDF
                    </Button>}
                </div>
            </div>

            <div className="tab-content-scrollable-container">
                <table className="tab__table">
                    <thead>
                        <tr>
                            {thArr.map((head) => (
                                <th key={head.short}>{isMobile() ? head.short : head.long}
                                    {head.key === 'array' && isNonIGSOpportunity() && !isReadOnly() && (
                                        <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 8, fontSize: 12 }}>
                                            <Switch
                                                size="small"
                                                checked={overrideEnabled}
                                                checkedChildren="Override"
                                                unCheckedChildren="Override"
                                                onChange={(checked) => {
                                                    if (checked) {
                                                        openOverrideAlert();
                                                    } else {
                                                        if (hasDuplicateArrays(equipmentList)) {
                                                            return sceneViewer.viewerAlert({
                                                                show: true,
                                                                title: "Cannot Disable Override",
                                                                message: "Some arrays are selected for multiple inverters.\nPlease unselect them before disabling override.",
                                                                messageType: AlertType.warning,
                                                                isCloseAfterDisplay: true,
                                                                isModal: false,
                                                            });
                                                        }
                                                        setOverrideEnabled(false);
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: overrideEnabled ? "#1890ff" : "#d9d9d9"
                                                }}
                                            />
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>

                        {/* EQUIPMENT TYPE */}
                        {(equipmentList).map((item, idx) => (
                            <tr key={item.name} style={{
                                border: "1px solid var(--border)",
                                height: "48px"
                            }}>
                                <td
                                    style={
                                        item.category === EquipmentType.Inverter
                                            ? {
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                border: "0px",
                                            }
                                            : {}
                                    }
                                >
                                    <span style={{ marginRight: "auto" }} className={
                                        item.category === EquipmentType.Inverter
                                            ? getArrayByRow().length > 1
                                                ? "multi-row-arrays"
                                                : "single-row-arrays"
                                            : ""
                                    }>{item.name}</span>
                                    {/* ADD/ REMOVE INVERTER BUTTONS */}
                                    {item.category === EquipmentType.Inverter && !isReadOnly() && (
                                        <>
                                            {idx === getInverterIndices()[0] && (
                                                <PlusCircleIcon
                                                    size={18}
                                                    color={isMaxInverter ? "grey" : "limegreen"}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={handleAddRow}
                                                />
                                            )}
                                            {idx !== getInverterIndices()[0] && (
                                                <MinusCircleIcon
                                                    size={18}
                                                    color="red"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => handleRemoveRow(item.name)}
                                                />
                                            )}
                                        </>
                                    )}
                                </td>

                                {/* EQUIPMENT MODEL */}
                                <td>
                                    {item.category === EquipmentType.Module
                                        ? (<span>{activeModule?.text || ""}</span>)
                                        : isReadOnly()
                                            ? <span>{item.model || ""}</span>
                                            : <Select
                                                value={item.model}
                                                onChange={(val) => handleChangeModel(item.name, item.category, val)}
                                            >
                                                {
                                                
                                                getOptions(item.category).map((opt, idx) => (
                                                    <Option key={idx} value={opt.model} >
                                                        {opt.model === '' ? '---- Select ----' : opt.model}
                                                    </Option>
                                                ))}
                                            </Select>
                                    }
                                </td>
                                {/* MANUFACTURER */}
                                <td style={{ width: '150px' }}>{item.manufacturer}</td>

                                {/* QUANTITY */}
                                <td align="center" style={{ width: '120px' }}>
                                    {isReadOnly() ? (
                                        <span>{item.quantity || ""}</span>
                                    ) :
                                        <InputNumber
                                            value={item.quantity || ''}
                                            onChange={val => {
                                                const num = Number(val);
                                                handleChangeQuantity(item.name, num > 0 ? num : 1);
                                            }}
                                            onFocus={() => useViewerStore.setState({ isModalOpenInInterface: true })}
                                            onBlur={() => {
                                                useViewerStore.setState({ isModalOpenInInterface: false });
                                                if (!item.quantity) handleChangeQuantity(item.name, 1)
                                            }}
                                            className="small-input-feild"
                                            style={{ width: '100px', alignContent: 'center' }}
                                            min={1}
                                            disabled={!item.model || item.category === EquipmentType.Module || item.category === EquipmentType.Optimizer}
                                        />}
                                </td>

                                {/* ARRAY NUMBER */}
                                <td style={{ width: '300px' }}>
                                    {item.category === EquipmentType.Inverter ? (
                                        <>
                                            {getArrayByRow().map((row, rowIdx) => (
                                                <div key={rowIdx} style={{ display: "flex", marginBottom: 4 }}>
                                                    {row.map(arrayNum => {
                                                        const isChecked = Array.isArray(item.roofNumbers) && item.roofNumbers.includes(arrayNum);
                                                        return (
                                                            <Checkbox
                                                                key={arrayNum}
                                                                checked={isChecked}
                                                                disabled={isArrayTaken(item.name, arrayNum)}
                                                                onChange={e => {
                                                                    if (isReadOnly()) return;
                                                                    const currentNumbers = Array.isArray(item.roofNumbers) ? item.roofNumbers : [];
                                                                    const newArr = e.target.checked
                                                                        ? [...currentNumbers, arrayNum]
                                                                        : currentNumbers.filter(n => n !== arrayNum);
                                                                    handleArrayChange(item.name, newArr);
                                                                }}
                                                                style={{ marginRight: 8, width: 36, display: "inline-flex", alignItems: "center" }}
                                                            >
                                                                {arrayNum}
                                                            </Checkbox>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="calculate-racking-quantities">
                {!isReadOnly() && <Button
                    loading={isLoadingQty}
                    title={"Calculate Racking Quantities"}
                    className={"packing-slip-table-save-button"}
                    type="primary"
                    onClick={handleCalculateRackingQuantities}
                    disabled={getDesigLockStatus()}
                    style={{ marginTop: 10, alignItems: "center", }}
                >
                    Calculate Racking Quantities
                </Button>}
            </div>
        </div>

    );
};

export default PackingSlip;